import { ImageView, View } from '@play-co/timestep-core/ui';
import i18n from 'src/lib/i18n/i18n';
import { Feature } from 'src/replicant/analytics';
import StateObserver from 'src/StateObserver';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { RaceConfig } from 'src/replicant/match2/races/races.ruleset';
import PopupMatch2Base, {
  PopupMatch2BaseInitOptions,
} from '../../components/PopupMatch2Base';
import { getRaceTheme } from '../races.utils';
import { RacesTimer } from '../components/RacesTimer';
import { animateEnergy } from 'src/game/animation/CurrencyAnimation';
import { AssetGroup } from '@play-co/timestep-core';

interface PopupMatch2RacesIntroInitOptions extends PopupMatch2BaseInitOptions {
  info?: boolean;
  config: RaceConfig;
}

export default class PopupMatch2RacesIntro extends PopupMatch2Base {
  private config!: RaceConfig;
  private image: ImageView;
  private text: LangBitmapFontTextView;

  constructor(opts: { superview: View; close: (success: boolean) => void }) {
    super({
      superview: opts.superview,
      width: 593,
      height: 814,
      panelImageOpts: {
        image: 'assets/ui/match2/common/panel_intro_alt.png',
      },
      offsetY: 45,
      close: () => opts.close(false),
      title: { type: 'image' },
      cta: {
        localeText: () => i18n('match2.races.play').toLocaleUpperCase(),
        positioning: 'edge',
        offsetY: -53,
        onClick: async () => {
          const position = this.buttonCta!.getPosition(this.root);
          position.x += this.buttonCta!.style.width * 0.5;
          position.y += this.buttonCta!.style.height * 0.5;
          if (this.config.type === 'time') {
            await animateEnergy({
              superview: this.root,
              start: {
                position,
                scale: 0.8,
              },
              count: 10,
            });
          }
          opts.close(true);
        },
      },
      tracking: {
        feature: Feature.MATCH2.RACES._,
        $subFeature: Feature.MATCH2.RACES.INTRO_POPUP._,
      },
    });

    this.image = new ImageView({
      superview: this.panelView,
      canHandleEvents: false,
      x: this.panel.style.width * 0.5,
      y: this.panel.style.height * 0.5 - 107,
      centerOnOrigin: true,
    });

    this.text = new LangBitmapFontTextView({
      superview: this.panel,
      font: 'Body',
      color: '#BF8147',
      width: this.panel.style.width * 0.9,
      height: 80,
      size: 22.5,
      x: this.panel.style.width * 0.5,
      y: 637,
      centerOnOrigin: true,
      align: 'center',
      verticalAlign: 'center',
      wordWrap: true,
    });

    new RacesTimer({
      superview: this.panel,
      centerOnOrigin: true,
      x: this.panel.style.width * 0.5,
      y: 557,
    });
  }

  protected getAssetsGroup() {
    // Load all assets if theme is somehow not set.
    const theme = this.config.theme ?? '';
    return new AssetGroup(
      AssetGroup.constructURLs([
        'assets/ui/match2/races/common',
        `assets/ui/match2/races/${theme}`,
      ]),
    );
  }

  public init({
    info,
    config,
    useClickOutside,
  }: PopupMatch2RacesIntroInitOptions) {
    if (info) {
      this.buttonCta?.hide();
    }
    this.config = config;
    this.setTitleImage({
      ...this.getPopupTitle(),
      centerOnOrigin: true,
      scaleMethod: 'contain',
    });
    this.text.localeText = () =>
      i18n(`match2.races.goals.${config.type}.${config.goal}`);
    this.image.updateOpts({
      ...this.getPopupBackground(),
      centerOnOrigin: true,
    });
    super.init({ useClickOutside });
  }

  /**
   * Get popup background
   */
  private getPopupBackground() {
    return getRaceTheme(this.config).intro.background;
  }

  /**
   * Get popup title
   */
  private getPopupTitle() {
    const locale = StateObserver.getLocale() === 'en' ? 'en' : 'jp';
    return getRaceTheme(this.config).title[locale];
  }
}
